import validations from "constants/validations";

import { useEffect, useRef, useState } from "react";

import { createAffiliatePage, getAffiliatePage, updateAffiliatePage } from "API/calls";
import { useFormik } from "formik";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { AffiliatePageState } from "types/enums";
import { IAffiliatePage } from "types/types";
import * as yup from "yup";

interface IFormikValues {
  path: string;
  nickname: string;
  title: string;
  description: string;
  commentary: string;
  imageDownloadUrl: string;
  youtubeId: string;
}

const extractYoutubeId = (input?: string) => {
  if (!input) return undefined;

  const match = input.match(
    /^(?:([a-zA-Z0-9_-]{11})|https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11}))/
  );
  if (!match) return "";

  return match[1] || match[2] || "";
};

const useNewAffiliatePageData = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(Boolean(id));
  const [isProcessing, setIsProcessing] = useState(false);
  const [detailData, setDetailData] = useState<IAffiliatePage | null>(null);
  const submitAction = useRef<"draft" | "redirect" | null>(null);

  const validationSchema = yup.object().shape({
    nickname: yup.string().required(t("fieldRequired")).max(40, t("fieldMaxLength", { max: 40 })),
    path: yup
      .string()
      .required(t("fieldRequired"))
      .matches(/^[a-z0-9-]+$/, t("affiliate.ownPage.form.pathValidationError"))
      .max(60, t("fieldMaxLength", { max: 60 })),
    title: yup
      .string()
      .required(t("fieldRequired"))
      .max(60, t("fieldMaxLength", { max: 60 })),
    imageDownloadUrl: yup.string().test("is-youtube-url", t("affiliate.ownPage.form.imageValidationError"), (value) => {
      if (!value) return true;
      return validations.urlAddress.test(value);
    }),
    youtubeId: yup.string().test("is-youtube-url", t("affiliate.ownPage.form.youtubeValidationError"), (value) => {
      if (!value) return true;
      return validations.youtubeUrl.test(value);
    }),
    description: yup.string().max(5000, t("fieldMaxLength", { max: 5000 })),
    commentary: yup.string().max(5000, t("fieldMaxLength", { max: 5000 })),
  });

  const formik = useFormik<IFormikValues>({
    initialValues: {
      path: "",
      nickname: "",
      title: "",
      description: "",
      commentary: "",
      imageDownloadUrl: "",
      youtubeId: "",
    },
    validationSchema: validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      setIsProcessing(true);

      if (id && detailData?.state === AffiliatePageState.DRAFT) {
        handleUpdate(values);
      } else handleCreate(values);
    },
  });

  const handleNavigate = (redirectId: string) => {
    if (submitAction.current === "draft") {
      navigate("/stosaky#affiliate");
    } else {
      window.location.href = `/doporucuje-nahled#${redirectId}`;
    }
  }

  const handleCreate = async (values: IFormikValues) => {
    try {
      const { data } = await createAffiliatePage({ ...values, youtubeId: extractYoutubeId(values.youtubeId) });
      enqueueSnackbar(t("affiliate.ownPage.form.success"), { variant: "success" });
      handleNavigate(data.id);
    } catch (error: any) {
      if (error?.response?.status === 409) {
        formik.setFieldError("path", t("affiliate.ownPage.form.pathAlreadyExists"));
      } else {
        enqueueSnackbar(t("affiliate.ownPage.form.error"), { variant: "error" });
      }

      setIsProcessing(false);
    }
  };

  const handleUpdate = async (values: IFormikValues) => {
    if (!id) return;

    try {
      await updateAffiliatePage(id, { ...values, youtubeId: extractYoutubeId(values.youtubeId) });
      enqueueSnackbar(t("affiliate.ownPage.form.updateSuccess"), { variant: "success" });
      handleNavigate(id);
    } catch (error: any) {
      if (error?.response?.status === 409) {
        formik.setFieldError("path", t("affiliate.ownPage.form.pathAlreadyExists"));
      } else {
        enqueueSnackbar(t("affiliate.ownPage.form.updateError"), { variant: "error" });
      }

      setIsProcessing(false);
    }
  };

  useEffect(() => {
    if (id) {
      const fetchDetail = async () => {
        try {
          const { data } = await getAffiliatePage(id);

          formik.resetForm({
            values: {
              path: data.path,
              nickname: data.nickname,
              title: data.title,
              description: data.description || "",
              commentary: data.commentary || "",
              imageDownloadUrl: data.imageDownloadUrl || "",
              youtubeId: data.youtubeId || "",
            },
          });

          setDetailData(data);
          setIsLoading(false);
        } catch (error) {
          enqueueSnackbar(t("affiliate.ownPage.form.notFound"), { variant: "error" });
          navigate("/stosaky#affiliate");
        }
      };
      fetchDetail();
    }
  }, [id]);

  const handleSubmit = (action: "draft" | "redirect") => () => {
    submitAction.current = action;
    formik.handleSubmit();
  };

  return {
    formik,
    isLoading,
    isProcessing,
    handleSubmit,
  };
};

export default useNewAffiliatePageData;
